import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package, Plus, Search, Edit, Trash2, ArrowUpCircle, ArrowDownCircle,
  AlertTriangle, Euro, Boxes, TrendingDown, TrendingUp, Truck, MoreHorizontal, Loader2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const CATEGORIES = ["Colore", "Styling", "Trattamento", "Accessori", "Rivendita", "Altro"];
const UNITS = [
  { value: "pz", label: "Pezzi" }, { value: "ml", label: "ml" },
  { value: "gr", label: "gr" }, { value: "lt", label: "Litri" },
  { value: "conf", label: "Confezioni" },
];
const REASONS = [
  { value: "internal_use", label: "Uso Interno" }, { value: "sale", label: "Vendita" },
  { value: "return", label: "Reso" }, { value: "waste", label: "Scarto" }, { value: "other", label: "Altro" },
];

const InventoryPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Dialogs
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isSupplierOpen, setIsSupplierOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);

  // Forms
  const emptyProduct = { name: '', brand: '', category: 'Altro', sku: '', purchase_price: '', sale_price: '', unit: 'pz', quantity: '', min_stock: '', supplier_id: '', notes: '' };
  const emptyMove = { product_id: '', movement_type: 'load', quantity: '', reason: '', unit_price: '', note: '', supplier_id: '' };
  const emptySupplier = { name: '', contact_name: '', email: '', phone: '', address: '', notes: '' };
  const [productForm, setProductForm] = useState(emptyProduct);
  const [moveForm, setMoveForm] = useState(emptyMove);
  const [supplierForm, setSupplierForm] = useState(emptySupplier);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, mRes, sRes, stRes] = await Promise.all([
        axios.get(`${API}/inventory/products`, getAuthHeaders()),
        axios.get(`${API}/inventory/movements?limit=50`, getAuthHeaders()),
        axios.get(`${API}/inventory/suppliers`, getAuthHeaders()),
        axios.get(`${API}/inventory/stats`, getAuthHeaders()),
      ]);
      setProducts(pRes.data); setMovements(mRes.data); setSuppliers(sRes.data); setStats(stRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchAll(); }, []);

  // Product CRUD
  const handleProductSave = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/inventory/products/${editingProduct.id}`, productForm, getAuthHeaders());
      } else {
        await axios.post(`${API}/inventory/products`, productForm, getAuthHeaders());
      }
      setIsProductOpen(false); setEditingProduct(null); setProductForm(emptyProduct); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({ name: p.name, brand: p.brand || '', category: p.category, sku: p.sku || '', purchase_price: p.purchase_price, sale_price: p.sale_price, unit: p.unit, quantity: p.quantity, min_stock: p.min_stock, supplier_id: p.supplier_id || '', notes: p.notes || '' });
    setIsProductOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Eliminare il prodotto e tutti i movimenti associati?')) return;
    await axios.delete(`${API}/inventory/products/${id}`, getAuthHeaders()); fetchAll();
  };

  // Movement
  const handleMoveSave = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/inventory/movements`, moveForm, getAuthHeaders());
      setIsMoveOpen(false); setMoveForm(emptyMove); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  // Supplier
  const handleSupplierSave = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/inventory/suppliers/${editingSupplier.id}`, supplierForm, getAuthHeaders());
      } else {
        await axios.post(`${API}/inventory/suppliers`, supplierForm, getAuthHeaders());
      }
      setIsSupplierOpen(false); setEditingSupplier(null); setSupplierForm(emptySupplier); fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const getSupplierName = (id) => suppliers.find(s => s.id === id)?.name || '';

  const filtered = useMemo(() => products.filter(p => {
    const q = searchQuery.toLowerCase();
    const match = p.name.toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q) || (p.sku || '').includes(q);
    const catMatch = categoryFilter === 'all' || p.category === categoryFilter;
    return match && catMatch;
  }), [products, searchQuery, categoryFilter]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Magazzino</h1>
          <p className="text-muted-foreground mt-1">Gestione prodotti, carico e scarico</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 sm:flex-initial" onClick={() => setIsMoveOpen(true)} data-testid="new-movement-btn"><ArrowUpCircle className="w-4 h-4 mr-2" />Movimento</Button>
          <Button className="flex-1 sm:flex-initial" onClick={() => { setEditingProduct(null); setProductForm(emptyProduct); setIsProductOpen(true); }} data-testid="new-product-btn"><Plus className="w-4 h-4 mr-2" />Prodotto</Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-2 sm:gap-4">
          <Card><CardContent className="p-2 sm:p-4 text-center"><Boxes className="w-5 h-5 mx-auto text-primary mb-1" /><p className="text-lg sm:text-2xl font-bold">{stats.total_products}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Prodotti</p></CardContent></Card>
          <Card><CardContent className="p-2 sm:p-4 text-center"><Euro className="w-5 h-5 mx-auto text-blue-500 mb-1" /><p className="text-lg sm:text-2xl font-bold">€{stats.total_stock_value.toFixed(0)}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Valore (Costo)</p></CardContent></Card>
          <Card><CardContent className="p-2 sm:p-4 text-center"><TrendingUp className="w-5 h-5 mx-auto text-emerald-500 mb-1" /><p className="text-lg sm:text-2xl font-bold">€{stats.total_sale_value.toFixed(0)}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Valore (Vendita)</p></CardContent></Card>
          <Card><CardContent className="p-2 sm:p-4 text-center"><AlertTriangle className={`w-5 h-5 mx-auto mb-1 ${stats.low_stock_count > 0 ? 'text-red-500' : 'text-muted-foreground'}`} /><p className={`text-2xl font-bold ${stats.low_stock_count > 0 ? 'text-red-500' : ''}`}>{stats.low_stock_count}</p><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">In Esaurimento</p></CardContent></Card>
        </div>
      )}

      {/* Low stock alerts */}
      {stats?.low_stock_products?.length > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-sm font-medium text-red-700">Prodotti sotto scorta minima</span></div>
            <div className="flex flex-wrap gap-2">
              {stats.low_stock_products.map(p => (
                <Badge key={p.id} variant="destructive" className="text-xs">{p.name}: {p.quantity} {p.unit} (min: {p.min_stock})</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="products">
        <TabsList className="w-full sm:w-auto flex justify-start gap-1 h-auto p-1 sm:inline-flex"><TabsTrigger value="products" className="gap-1.5"><Package className="w-4 h-4" /><span>Prodotti</span></TabsTrigger>
          <TabsTrigger value="movements" className="gap-1.5"><ArrowUpCircle className="w-4 h-4" /><span>Movimenti</span></TabsTrigger>
          <TabsTrigger value="suppliers" className="gap-1.5"><Truck className="w-4 h-4" /><span>Fornitori</span></TabsTrigger>
        </TabsList>

        {/* ─── PRODUCTS TAB ─── */}
        <TabsContent value="products" className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="Cerca prodotti..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-products" /></div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}><SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Categoria" /></SelectTrigger><SelectContent><SelectItem value="all">Tutte</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(p => {
                const isLow = p.min_stock > 0 && p.quantity <= p.min_stock;
                return (
                  <Card key={p.id} className={isLow ? 'border-red-300' : ''} data-testid={`product-${p.id}`}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate">{p.name}</h3>
                          {p.brand && <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">{p.brand}</p>}
                        </div>
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditProduct(p)}><Edit className="w-4 h-4 mr-2" />Modifica</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setMoveForm({ ...emptyMove, product_id: p.id }); setIsMoveOpen(true); }}><ArrowUpCircle className="w-4 h-4 mr-2" />Movimento</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteProduct(p.id)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" />Elimina</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center gap-2"><Badge variant="secondary" className="text-[10px]">{p.category}</Badge>{p.sku && <span className="text-[10px] text-muted-foreground font-mono">{p.sku}</span>}</div>
                      <div className="flex items-end justify-between pt-1">
                        <div><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Quantità</p><p className={`text-xl font-bold ${isLow ? 'text-red-500' : ''}`}>{p.quantity} <span className="text-xs font-normal text-muted-foreground">{p.unit}</span></p></div>
                        <div className="text-right"><p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">Costo / Vendita</p><p className="text-sm">€{p.purchase_price.toFixed(2)} / <strong>€{p.sale_price.toFixed(2)}</strong></p></div>
                      </div>
                      {isLow && <div className="flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="w-3 h-3" />Sotto soglia minima ({p.min_stock} {p.unit})</div>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card><CardContent className="py-16 text-center"><Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold mb-1">Nessun prodotto</h3><p className="text-sm text-muted-foreground">Aggiungi il tuo primo prodotto per iniziare.</p></CardContent></Card>
          )}
        </TabsContent>

        {/* ─── MOVEMENTS TAB ─── */}
        <TabsContent value="movements" className="mt-4">
          {movements.length > 0 ? (
            <div className="space-y-2">
              {movements.map(m => (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`movement-${m.id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${m.movement_type === 'load' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                    {m.movement_type === 'load' ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{m.product_name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{m.movement_type === 'load' ? 'Carico' : 'Scarico'}</span>
                      {m.reason && <span>· {REASONS.find(r => r.value === m.reason)?.label || m.reason}</span>}
                      {m.note && <span>· {m.note}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold text-sm ${m.movement_type === 'load' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {m.movement_type === 'load' ? '+' : '-'}{m.quantity}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card><CardContent className="py-16 text-center"><ArrowUpCircle className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold mb-1">Nessun movimento</h3><p className="text-sm text-muted-foreground">Registra carichi e scarichi dal bottone "Movimento".</p></CardContent></Card>
          )}
        </TabsContent>

        {/* ─── SUPPLIERS TAB ─── */}
        <TabsContent value="suppliers" className="mt-4 space-y-4">
          <div className="flex justify-end"><Button variant="outline" onClick={() => { setEditingSupplier(null); setSupplierForm(emptySupplier); setIsSupplierOpen(true); }} data-testid="new-supplier-btn"><Plus className="w-4 h-4 mr-2" />Fornitore</Button></div>
          {suppliers.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {suppliers.map(s => (
                <Card key={s.id} data-testid={`supplier-${s.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div><h3 className="font-semibold">{s.name}</h3>{s.contact_name && <p className="text-sm text-muted-foreground">{s.contact_name}</p>}</div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingSupplier(s); setSupplierForm({ name: s.name, contact_name: s.contact_name || '', email: s.email || '', phone: s.phone || '', address: s.address || '', notes: s.notes || '' }); setIsSupplierOpen(true); }}><Edit className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async () => { if (window.confirm('Eliminare?')) { await axios.delete(`${API}/inventory/suppliers/${s.id}`, getAuthHeaders()); fetchAll(); } }}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                      {s.email && <span>{s.email}</span>}{s.phone && <span>{s.phone}</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card><CardContent className="py-12 text-center"><Truck className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold mb-1">Nessun fornitore</h3></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ─── PRODUCT DIALOG ─── */}
      <Dialog open={isProductOpen} onOpenChange={(o) => { if (!o) { setIsProductOpen(false); setEditingProduct(null); } else setIsProductOpen(true); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-outfit">{editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle></DialogHeader>
          <form onSubmit={handleProductSave} className="space-y-4 mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Nome *</Label><Input value={productForm.name} onChange={e => setProductForm(p => ({ ...p, name: e.target.value }))} required data-testid="prod-name" /></div>
              <div className="space-y-1"><Label>Marca</Label><Input value={productForm.brand} onChange={e => setProductForm(p => ({ ...p, brand: e.target.value }))} data-testid="prod-brand" /></div>
              <div className="space-y-1"><Label>Categoria</Label><Select value={productForm.category} onValueChange={v => setProductForm(p => ({ ...p, category: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>SKU</Label><Input value={productForm.sku} onChange={e => setProductForm(p => ({ ...p, sku: e.target.value }))} placeholder="Codice prodotto" /></div>
              <div className="space-y-1"><Label>Prezzo Acquisto (€)</Label><Input type="number" step="0.01" min="0" value={productForm.purchase_price} onChange={e => setProductForm(p => ({ ...p, purchase_price: e.target.value }))} data-testid="prod-purchase-price" /></div>
              <div className="space-y-1"><Label>Prezzo Vendita (€)</Label><Input type="number" step="0.01" min="0" value={productForm.sale_price} onChange={e => setProductForm(p => ({ ...p, sale_price: e.target.value }))} data-testid="prod-sale-price" /></div>
              <div className="space-y-1"><Label>Unità</Label><Select value={productForm.unit} onValueChange={v => setProductForm(p => ({ ...p, unit: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Quantità Iniziale</Label><Input type="number" min="0" value={productForm.quantity} onChange={e => setProductForm(p => ({ ...p, quantity: e.target.value }))} data-testid="prod-quantity" /></div>
              <div className="space-y-1"><Label>Scorta Minima</Label><Input type="number" min="0" value={productForm.min_stock} onChange={e => setProductForm(p => ({ ...p, min_stock: e.target.value }))} data-testid="prod-min-stock" /></div>
              <div className="space-y-1"><Label>Fornitore</Label><Select value={productForm.supplier_id || 'none'} onValueChange={v => setProductForm(p => ({ ...p, supplier_id: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger><SelectContent><SelectItem value="none">Nessuno</SelectItem>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="space-y-1"><Label>Note</Label><Textarea value={productForm.notes} onChange={e => setProductForm(p => ({ ...p, notes: e.target.value }))} rows={2} /></div>
            <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsProductOpen(false)}>Annulla</Button><Button type="submit" data-testid="save-product-btn">{editingProduct ? 'Aggiorna' : 'Crea'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── MOVEMENT DIALOG ─── */}
      <Dialog open={isMoveOpen} onOpenChange={setIsMoveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-outfit">Registra Movimento</DialogTitle></DialogHeader>
          <form onSubmit={handleMoveSave} className="space-y-4 mt-3">
            <div className="space-y-2">
              <Label>Prodotto *</Label>
              <Select value={moveForm.product_id || 'none'} onValueChange={v => setMoveForm(p => ({ ...p, product_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="move-product"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.quantity} {p.unit})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select value={moveForm.movement_type} onValueChange={v => setMoveForm(p => ({ ...p, movement_type: v }))}>
                  <SelectTrigger data-testid="move-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="load">Carico</SelectItem><SelectItem value="unload">Scarico</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Quantità *</Label><Input type="number" min="0.01" step="0.01" value={moveForm.quantity} onChange={e => setMoveForm(p => ({ ...p, quantity: e.target.value }))} required data-testid="move-quantity" /></div>
            </div>
            {moveForm.movement_type === 'unload' && (
              <div className="space-y-2"><Label>Motivo</Label><Select value={moveForm.reason || 'none'} onValueChange={v => setMoveForm(p => ({ ...p, reason: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Seleziona motivo" /></SelectTrigger><SelectContent>{REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            )}
            {moveForm.movement_type === 'load' && (
              <div className="space-y-2"><Label>Prezzo unitario (€)</Label><Input type="number" step="0.01" min="0" value={moveForm.unit_price} onChange={e => setMoveForm(p => ({ ...p, unit_price: e.target.value }))} /></div>
            )}
            <div className="space-y-2"><Label>Nota</Label><Input value={moveForm.note} onChange={e => setMoveForm(p => ({ ...p, note: e.target.value }))} placeholder="Nota opzionale" data-testid="move-note" /></div>
            <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsMoveOpen(false)}>Annulla</Button><Button type="submit" data-testid="save-movement-btn">Registra</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── SUPPLIER DIALOG ─── */}
      <Dialog open={isSupplierOpen} onOpenChange={setIsSupplierOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-outfit">{editingSupplier ? 'Modifica Fornitore' : 'Nuovo Fornitore'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSupplierSave} className="space-y-3 mt-3">
            <div className="space-y-1"><Label>Nome *</Label><Input value={supplierForm.name} onChange={e => setSupplierForm(p => ({ ...p, name: e.target.value }))} required data-testid="supplier-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Referente</Label><Input value={supplierForm.contact_name} onChange={e => setSupplierForm(p => ({ ...p, contact_name: e.target.value }))} /></div>
              <div className="space-y-1"><Label>Email</Label><Input type="email" value={supplierForm.email} onChange={e => setSupplierForm(p => ({ ...p, email: e.target.value }))} /></div>
              <div className="space-y-1"><Label>Telefono</Label><Input value={supplierForm.phone} onChange={e => setSupplierForm(p => ({ ...p, phone: e.target.value }))} /></div>
              <div className="space-y-1"><Label>Indirizzo</Label><Input value={supplierForm.address} onChange={e => setSupplierForm(p => ({ ...p, address: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setIsSupplierOpen(false)}>Annulla</Button><Button type="submit" data-testid="save-supplier-btn">{editingSupplier ? 'Aggiorna' : 'Crea'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
